// 커뮤니티 작성자 차단 목록 어댑터
//
// ⚠ S-14 (2026-07-30): 이전에는 목록이 **기기 저장소에만** 있었다. UI는
//   "앞으로 이 사람의 글·댓글이 보이지 않아요"라고 약속하는데, 앱 데이터를 지우거나
//   새 단말에서 로그인하면 차단이 통째로 풀렸다.
//   → 서버 `user_blocks`가 정본이고 Preferences는 **오프라인·즉시 렌더용 캐시**다.
//   서버 조회에 실패하면 캐시로 동작한다(차단이 갑자기 풀린 것처럼 보이지 않게).
import { Preferences } from '@capacitor/preferences';
import { supabase } from '../lib/supabase';

const KEY_PREFIX = 'sotda_blocked_authors_';
const keyFor = (userId: string) => KEY_PREFIX + userId;

// userId별 동기 조회 캐시 (렌더 필터에서 await 없이 쓰기 위함)
const cacheByUser: Record<string, Set<string>> = {};

/** 기기 저장소에 남은 캐시 — 서버 응답 전/실패 시 쓰는 값. */
async function readCache(userId: string): Promise<Set<string>> {
  try {
    const { value } = await Preferences.get({ key: keyFor(userId) });
    return new Set<string>(value ? JSON.parse(value) : []);
  } catch {
    return new Set<string>();
  }
}

/** 차단 목록 로드 (계정별) — userId 없으면(게스트) 빈 Set.
 *  서버가 정본. 실패하면 캐시로 폴백한다(S-14). */
export async function getBlockedAuthors(userId?: string | null): Promise<Set<string>> {
  if (!userId) return new Set<string>();
  if (cacheByUser[userId]) return cacheByUser[userId];

  const cached = await readCache(userId);
  try {
    const { data, error } = await supabase
      .from('user_blocks').select('blocked_id').eq('user_id', userId);
    // Supabase는 실패해도 throw가 아니라 { error }로 resolve한다 → 반환값을 본다.
    if (!error && Array.isArray(data)) {
      const server = new Set<string>(data.map((r: any) => r.blocked_id).filter(Boolean));
      // 기기에만 있던 과거 차단은 서버로 올려 단말 간에 유지되게 한다(1회성 이관).
      const onlyLocal = [...cached].filter((id) => !server.has(id));
      if (onlyLocal.length) {
        await supabase.from('user_blocks')
          .upsert(onlyLocal.map((id) => ({ user_id: userId, blocked_id: id })), { onConflict: 'user_id,blocked_id' });
        onlyLocal.forEach((id) => server.add(id));
      }
      cacheByUser[userId] = server;
      await persistCacheOnly(userId, server);
      return server;
    }
  } catch {
    /* 오프라인 등 — 아래 캐시 폴백 */
  }
  cacheByUser[userId] = cached;
  return cached;
}

/** 작성자 차단 (계정별) — 서버에 먼저 기록하고 캐시를 갱신한다. */
export async function blockAuthor(userId: string, authorId: string): Promise<void> {
  if (!userId || !authorId || userId === authorId) return;
  const set = await getBlockedAuthors(userId);
  set.add(authorId);
  cacheByUser[userId] = set;
  try {
    await supabase.from('user_blocks')
      .upsert({ user_id: userId, blocked_id: authorId }, { onConflict: 'user_id,blocked_id' });
  } catch {
    /* 오프라인 — 캐시에는 남으므로 다음 로드 때 서버로 이관된다(위 onlyLocal 경로) */
  }
  await persistCacheOnly(userId, set);
}

/** 차단 해제 (계정별) */
export async function unblockAuthor(userId: string, authorId: string): Promise<void> {
  if (!userId) return;
  const set = await getBlockedAuthors(userId);
  set.delete(authorId);
  cacheByUser[userId] = set;
  try {
    await supabase.from('user_blocks').delete().eq('user_id', userId).eq('blocked_id', authorId);
  } catch {
    /* 오프라인 — 캐시에서는 빠지지만 서버엔 남는다. 다음 로드 때 서버 값이 정본이라 되살아난다.
       (차단이 조용히 풀리는 것보다 안전한 방향이다) */
  }
  await persistCacheOnly(userId, set);
}

async function persistCacheOnly(userId: string, set: Set<string>): Promise<void> {
  try {
    await Preferences.set({ key: keyFor(userId), value: JSON.stringify([...set]) });
  } catch {
    /* 저장 실패는 무시 — 서버가 정본이라 다음 로드에서 복구된다 */
  }
}

/** 차단 목록 + 닉네임 (MY 탭 차단 관리 UI용) — profiles 조회로 닉 보강 */
export async function getBlockedList(userId?: string | null): Promise<{ id: string; nickname: string }[]> {
  const set = await getBlockedAuthors(userId);
  const ids = [...set];
  if (ids.length === 0) return [];
  let nameMap: Record<string, string> = {};
  try {
    const { data } = await supabase.from('profiles').select('id, nickname').in('id', ids);
    nameMap = Object.fromEntries((data || []).map((p: any) => [p.id, p.nickname]));
  } catch { /* 조회 실패 시 id만 노출 */ }
  return ids.map((id) => ({ id, nickname: nameMap[id] || '(알 수 없는 사용자)' }));
}
